import DownloadIcon from '@offline-mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useState } from 'react'
import { accountingRequest, accountingUrl, jsonInit } from '../api'
import { LeoHeader } from '../LeoHeader'

const COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice' },
  { key: 'recordLocator', label: 'PNR' },
  { key: 'ticketNumber', label: 'Ticket No.' },
  { key: 'cost', label: 'Cost', money: true },
  { key: 'payable', label: 'Payable', money: true },
  { key: 'creditCardSales', label: 'Credit Card Sales', money: true },
  { key: 'cashSales', label: 'Cash Sales', money: true },
]

function formatAmount(value: unknown) {
  if (value == null || value === '') return ''
  const number = Number(value)
  const text = Math.abs(number).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return number < 0 ? `-${text}` : text
}

function BspReport() {
  const [report, setReport] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function run() {
    setLoading(true)
    setError('')
    try {
      setReport(await accountingRequest('/reports/bsp'))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    run()
  }, [])

  const rows: any[] = report?.rows || []
  const totals = report?.totals

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>BSP reconciliation</Typography>
        {report ? <Chip size="small" label={`${rows.length} ticket${rows.length === 1 ? '' : 's'}`} /> : null}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="warning" disabled={loading} onClick={run}>Refresh</Button>
        <Button
          component="a"
          href={accountingUrl('/reports/bsp.csv')}
          variant="contained"
          color="secondary"
          startIcon={<DownloadIcon />}
          disabled={loading || rows.length === 0}
        >
          Download CSV
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Supplier lines whose supplier name starts with BSP. Cost is the total fare less our commission.
        Card: Payable is our commission shown negative, and Credit Card Sales is the amount the airline charged.
        Cheque: Payable and Cash Sales both equal the cost.
      </Typography>
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      <TableContainer component={Card} sx={{ mt: 2 }}>
        {loading ? <LinearProgress color="warning" /> : null}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {COLUMNS.map((column) => (
                <TableCell key={column.key} align={column.money ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={`${row.invoiceNumber}-${row.ticketNumber}-${index}`} sx={{ bgcolor: index % 2 ? 'rgba(29, 78, 137, 0.06)' : 'background.paper' }}>
                {COLUMNS.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.money ? 'right' : 'left'}
                    sx={{
                      fontWeight: column.key === 'invoiceNumber' ? 700 : 400,
                      color: column.money && Number(row[column.key]) < 0 ? 'error.main' : 'text.primary',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {column.money ? formatAmount(row[column.key]) : row[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length}>
                  <Typography variant="body2" color="text.secondary">No BSP lines yet.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {totals && rows.length > 0 ? (
              <TableRow sx={{ bgcolor: 'rgba(233, 180, 76, 0.18)' }}>
                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                <TableCell />
                <TableCell />
                {['cost', 'payable', 'creditCardSales', 'cashSales'].map((key) => (
                  <TableCell key={key} align="right" sx={{ fontWeight: 700, color: Number(totals[key]) < 0 ? 'error.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                    {formatAmount(totals[key])}
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

// SND-14218 / 14219 / 14221: each Create click stores a run on the server; the tab shows the latest run.
const FINANCE_REPORTS = [
  { key: 'jdv', code: 'JDV' },
  { key: 'jrm', code: 'JRM' },
  { key: 'jda', code: 'JDA' },
  { key: 'jdd', code: 'JDD' },
]

function isoDate(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}

function FinanceReport({ reportKey, code }: { reportKey: string; code: string }) {
  const today = new Date()
  const [from, setFrom] = useState(isoDate(new Date(today.getFullYear(), today.getMonth(), 1)))
  const [to, setTo] = useState(isoDate(today))
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    accountingRequest(`/reports/${reportKey}/latest`)
      .then((result) => {
        if (active) setData(result)
      })
      .catch((err) => {
        if (active) setError(err.message)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [reportKey])

  async function create() {
    if (from && to && from > to) {
      setError('From date must be on or before the to date.')
      return
    }
    setCreating(true)
    setError('')
    try {
      setData(await accountingRequest(`/reports/${reportKey}/runs`, jsonInit('POST', { from: from || null, to: to || null })))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const columns: any[] = data?.columns || []
  const rows: any[] = data?.rows || []
  const totals = data?.totals || {}
  const run = data?.run
  const firstMoney = columns.findIndex((column) => column.money)

  return (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {code}{data?.title ? ` — ${data.title}` : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button variant="contained" color="warning" disabled={creating} onClick={create}>
          {creating ? 'Creating…' : `Create ${code} Report`}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {data?.dateRule ? `Dates: ${data.dateRule} ` : ''}Empty cells are fields we do not store yet.
      </Typography>
      {run ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip size="small" color="info" label={`Run #${run.id}`} />
          <Chip size="small" label={`${run.from || 'start'} to ${run.to || 'today'}`} />
          <Chip size="small" label={`${run.rowCount} row${run.rowCount === 1 ? '' : 's'}`} />
          <Chip size="small" variant="outlined" label={`Created ${String(run.createdAt || '').replace('T', ' ').slice(0, 16)}`} />
        </Stack>
      ) : null}
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      <TableContainer component={Card} sx={{ mt: 2 }}>
        {loading || creating ? <LinearProgress color="warning" /> : null}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.money ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.id || index} sx={{ bgcolor: index % 2 ? 'rgba(29, 78, 137, 0.06)' : 'background.paper' }}>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.money ? 'right' : 'left'}
                    sx={{
                      fontWeight: column.key === 'invoiceNumber' ? 700 : 400,
                      color: column.money && Number(row[column.key]) < 0 ? 'error.main' : 'text.primary',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: column.key === 'txnDate' ? 'nowrap' : undefined,
                    }}
                  >
                    {column.money ? formatAmount(row[column.key]) : row[column.key] ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!loading && !creating && columns.length > 0 && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    {run ? 'This run has no rows for its date range.' : `No ${code} report yet. Pick a date range and create one.`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {rows.length > 0 && firstMoney > 0 ? (
              <TableRow sx={{ bgcolor: 'rgba(233, 180, 76, 0.18)' }}>
                <TableCell colSpan={firstMoney} sx={{ fontWeight: 700 }}>Total</TableCell>
                {columns.slice(firstMoney).map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.money ? 'right' : 'left'}
                    sx={{ fontWeight: 700, color: Number(totals[column.key]) < 0 ? 'error.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {column.money ? formatAmount(totals[column.key]) : ''}
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default function ReportsPage() {
  const [tab, setTab] = useState('bsp')
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
      <LeoHeader sx={{ mb: 3 }} />
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="bsp" label="BSP" />
        {FINANCE_REPORTS.map((report) => (
          <Tab key={report.key} value={report.key} label={report.code} />
        ))}
      </Tabs>
      {tab === 'bsp' ? <BspReport /> : null}
      {FINANCE_REPORTS.map((report) =>
        tab === report.key ? <FinanceReport key={report.key} reportKey={report.key} code={report.code} /> : null,
      )}
    </Box>
  )
}
